//imports
import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Toolbar,
  Snackbar,
  Stack,
  Alert,
  Breadcrumbs,
  Link,
  AppBar,
  Divider, // Import Alert
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import PrintIcon from "@mui/icons-material/Print";
import { externalgetApi } from "../../services/external/externalApis";
import { useNavigate, useLocation } from "react-router-dom";
import {
  REPORT_API_02,
  Dayend_API_01,
  LOCATION_01,
  REST_01,
} from "../../api/ApiModel";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme";
import { currencyFormat } from "../../../src/utils/common/CurrencyFormat";
import Navbar from "../../components/common/Navbar";
import { FileDownload } from "@mui/icons-material"; // Assuming you're using MUI icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

//hooks
const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = String(currentDate.getMonth() + 1).padStart(2, "0");

const startOfMonth = dayjs().startOf("month");

const endOfMonthDate = dayjs().endOf("month");
function ReportView() {
  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [reportDetails, setReportDetails] = useState([]);
  const [columns, setColumns] = useState([]);
  const [locations, setLocations] = useState([]); // Stores the list of locations
  const [restaurants, setRestaurants] = useState([]); // Stores the list of restaurants
  const [selectedLocation, setSelectedLocation] = useState(""); // Stores selected location
  const [selectedLocCode, setSelectedLocCode] = useState(""); // Stores selected location code
  const [selectedRestaurant, setSelectedRestaurant] = useState(); // Stores selected restaurant code

  const [fromDate, setFromDate] = useState(startOfMonth);
  const [toDate, setToDate] = useState(endOfMonthDate);

  const [error, setError] = useState("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false); // Snackbar state
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);
  const locationState = useLocation();
  const { reportId } = locationState.state || {};

  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, GROUPCODE, RESTCODE, LOCCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  useEffect(() => {
    fetchData();
    fetchRestaurantData(); // Fetch data when component mounts
  }, []);

  //handlers
  const handleDateChange = (newValue) => {
    if (newValue) {
      // Format the date to YYYY-MM-DD before sending to API
      const formattedDate = newValue.format("MM/DD/YYYY");
      setFromDate(dayjs(formattedDate));
    } else {
      setFromDate(""); // Handle null case
    }
  };

  const handleToDateChange = (newValue) => {
    if (newValue) {
      // Format the date to YYYY-MM-DD before sending to API
      const formattedDate = newValue.format("MM/DD/YYYY");
      setToDate(dayjs(formattedDate));
    } else {
      setToDate(""); // Handle null case
    }
  };

  const shouldFormatCurrency = (field) => {
    const currencyFields = [
      "GIFTAMOUNT",
      "LOYALTYAMOUNT",
      "AMOUNT",
      "ADVANCEAMOUNT",
      "SHIPPINGAMOUNT",
      "ADJUSTAMOUNT",
      "BILLAMOUNT",
      "PAIDAMOUNT",
      "GROSSAMOUNT",
      "RATE",
      "PAIDAMOUNT",
      "TOTAL",
      "GRNRATE",
      "CARDAMOUNT",
      "TAXAMT",
      "DISCOUNT",
      "ADJUSTAMOUNT",
      "NETTAMT",
      "CHARGEAMOUNT",
      "SELLINGRATE",
      "TAXAMOUNT",
      "CHARGEAMOUNT",
      "GROSSAMT",
      "CASHAMOUNT",
      "CHEQUEAMOUNT",
      "SLIPAMOUNT",
      "CASHINHAND",
      "CASHORDER",
      "AVGCOST",
      "DIFFERENCE",
      "OPENNINGBAL",
      "CASHDEPOSIT",
      "CASHSALES",
      "FINALBAL",
    ];
    return currencyFields.includes(field);
  };

  const countOnQty = (field) => {
    const qtyFields = [
      "QTY",
      "TOTSALES", // Add other fields that need to be summed dynamically here
    ];
    return qtyFields.includes(field);
  };
  const shouldFormatdate = (field) => {
    const dateFields = [
      "PAYDATE",
      "ORDERENDATE",
      "DEPOSITDATE",
      "INVOICEDATE",
      "ENDATE",
      "EXPIRY",
    ];
    return dateFields.includes(field);
  };

  const renderCellValue = (field, value) => {
    if (shouldFormatCurrency(field)) {
      return currencyFormat(value);
    } else if (shouldFormatdate(field)) {
      return formatDate(value);
    }
    return value; // Return the value as-is if no formatting is needed
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await externalgetApi(LOCATION_01, "get", {
        COMPCODE,
        GROUPCODE,
        USERNAME,
        STS: CON_STATUS_ACTIVE,
      });

      if (result && result.length > 0) {
        setLocations(
          result.map((item) => ({
            Location: item.LOCATION,
            loccode: item.LOCCODE,
          }))
        );
      }
    } catch (err) {
      handleError("Error fetching locations");
    } finally {
      setIsLoading(false);
    }
  };

  //get api
  const fetchRestaurantData = async () => {
    try {
      setIsLoading(true);
      const result = await externalgetApi(REST_01, "get", {
        COMPCODE,
        LOCCODE,
        CATTYPE: "P",
        STS: CON_STATUS_ACTIVE,
      });

      if (result && result.length > 0) {
        setRestaurants(
          result.map((item) => ({
            restname: item.RESTNAME,
            restcode: item.RESTCODE, // Storing RESTCODE to use as the value
          }))
        );
      }
    } catch (err) {
      handleError("Error fetching restaurants");
    } finally {
      setIsLoading(false);
    }
  };

  const gotToView = () => {
    navigate("/Reports");
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    STOCKIN: false,
    STOCKOUT: false,
  });

  const handleError = (errorMessage) => {
    setError(errorMessage);
    setIsSnackbarOpen(true); // Open Snackbar
  };

  const handleLocationChange = (e) => {
    const selectedLocation = e.target.value;
    setSelectedLocation(selectedLocation);

    const selectedLocCode = locations.find(
      (loc) => loc.Location === selectedLocation
    )?.loccode;
    setSelectedLocCode(selectedLocCode);

    // After location change, fetch restaurants
    fetchRestaurantData();
  };

  // Handle restaurant selection change
  const handleRestaurantChange = (e) => {
    setSelectedRestaurant(e.target.value);
  };
  const fetchReportDetails = async () => {
    if (!fromDate || !toDate) {
      setSnackbarMessage(
        "Please select a location, restaurant, start, and end dates."
      );
      setSnackbarOpen(true);
      return;
    }

    try {
      setReportDetails([]);
      setIsLoading(true);
      setError("");

      const params = {
        COMPCODE,
        // LOCCODE: selectedLocCode,
        // RESTCODE: selectedRestaurant || "0",
        LOCCODE,
        RESTCODE,
        REPORTID: reportId,
        FROMDATE: dayjs(fromDate).format("MM-DD-YYYY"),
        TODATE: dayjs(toDate).format("MM-DD-YYYY"),
        DATE: new Date().toISOString().split("T")[0],
        STS: CON_STATUS_ACTIVE,
        USERNAME,
      };

      console.log("API Params:", params);

      const result = await externalgetApi(REPORT_API_02, "get", params);

      if (!result || result.length === 0) {
        setSnackbarMessage("No data found for the selected date range.");
        setSnackbarOpen(true);
      } else {
        setReportDetails(result);
      }
    } catch (err) {
      console.error("Error fetching report details:", err);
      setSnackbarMessage("An unexpected error occurred. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return timestamp.split("T")[0];
  };

  useEffect(() => {
    if (reportId) {
      const { columns: dynamicColumns, reportName: dynamicReportName } =
        getReportConfig(reportId);
      setColumns(dynamicColumns);
      setReports(dynamicReportName);
    }
  }, [reportId]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const calculateQty = () => {
    const totalsqty = {}; // Initialize totals as an empty object

    reportDetails.forEach((row) => {
      columns.forEach((col) => {
        if (countOnQty(col.field)) {
          const value = parseFloat(row[col.field]) || 0;
          totalsqty[col.field] = (totalsqty[col.field] || 0) + value;
        }
      });
    });

    return totalsqty; // Return the populated totals object
  };

  const totalsqty = calculateQty(); // Call the function and store the result

  const calculateTotals = () => {
    const totals = {};
    reportDetails.forEach((row) => {
      columns.forEach((col) => {
        if (shouldFormatCurrency(col.field)) {
          const value = parseFloat(row[col.field]) || 0;
          totals[col.field] = (totals[col.field] || 0) + value;
        }
      });
    });
    return totals;
  };

  const totals = calculateTotals();

  const calculateRowDifference = (row, col1, col2) => {
    const value1 = row[col1] || 0; // Default to 0 if undefined
    const value2 = row[col2] || 0; // Default to 0 if undefined
    return value1 - value2; // Return the difference
  };

  const calculateTotalDifference = (rows, field1, field2) => {
    return rows.reduce((total, row) => {
      const difference = calculateRowDifference(row, field1, field2); // Calculate the difference for each row
      return total + difference; // Sum up the differences
    }, 0);
  };

  const totalsRow = columns.reduce(
    (acc, col) => {
      const totalValue = totals[col.field];
      const totalcount = totalsqty[col.field];

      //  console.log(`Total for ${col.field}:`, totalValue); // Debug log to check the value

      // Ensure it's a valid number
      const parsedValue = totalValue;

      // Check if the field should be formatted as currency
      if (shouldFormatCurrency(col.field)) {
        acc[col.field] = totalValue || "00.0";
      } else if (countOnQty(col.field)) {
        acc[col.field] = totalcount || "0"; // Default to an empty string if no value
      } else {
        // If it's neither a currency nor a summed field, assign the parsed value
        acc[col.field] = parsedValue || "";
      }

      return acc;
    },
    { id: "Total" }
  );

  totalsRow.DIFFERENCE = calculateTotalDifference(
    reportDetails,
    "AVGCOST",
    "RATE"
  );
  totalsRow.STOCKINHAND = calculateTotalDifference(
    reportDetails,
    "STOCKIN",
    "STOCKOUT"
  );

  // Function to check if a field should be counted
  const countons = (field) => {
    const currencyFields = [
      "ORDERCODE", // Add any other fields to count dynamically here
    ];
    return currencyFields.includes(field);
  };

  // Function to calculate lengths of specific fields
  const calculateLengths = () => {
    const lengths = {};

    reportDetails.forEach((row) => {
      columns.forEach((col) => {
        if (countons(col.field)) {
          const value = row[col.field]; // Access the value using col.field

          // Only increment the count if the value exists
          if (value !== undefined && value !== null) {
            lengths[col.field] = (lengths[col.field] || 0) + 1;
          }
        }
      });
    });

    return lengths;
  };

  const lengths = calculateLengths();

  const convertToCSV = () => {
    // Column headers
    const headers = columns.map((col) => col.headerName).join(",");

    // Data rows
    const dataRows = reportDetails.map((row) =>
      columns.map((col) => row[col.field] || "").join(",")
    );

    // Add totals row if needed
    const totalsRow =
      reportId === 41 ||
      reportId === 43 ||
      reportId === 44 ||
      reportId === 49 ||
      reportId === 50 ||
      reportId === 51 ||
      reportId === 98 ||
      reportId === 99 ||
      reportId === 118 ||
      reportId === 107 ||
      reportId === 99
        ? columns
            .map((col) => (totals[col.field] ? totals[col.field] : ""))
            .join(",")
        : "";

    // Adding 'Total orders' row for reportId 41
    const totalOrdersRow =
      reportId === 41 ||
      reportId === 43 ||
      reportId === 50 ||
      reportId === 44 ||
      reportId === 98 ||
      reportId === 107 ||
      reportId === 118
        ? `Total orders,${lengths["ORDERCODE"] || 0}`
        : "";

    // Combine headers, data rows, totals row, and total orders row
    const csvContent = [headers, ...dataRows, totalsRow, totalOrdersRow].join(
      "\n"
    );

    return csvContent;
  };

  // Function to trigger file download
  const downloadCSV = () => {
    const csvContent = convertToCSV();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      // Feature detection for download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "report.csv");
      link.click();
    }
  };

  // const currencyFormat = (value) => {
  //   return `${parseFloat(value).toFixed(2)}`;
  // };
  const getReportConfig = (id) => {
    switch (String(id)) {
      case "133":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 },
            {
              field: "ORDERENDATE",
              headerName: " Date",
              align: "left",
            },
            {
              field: "INVNO",
              headerName: "Invoice Number",
              align: "center",
            },
            {
              field: "BILLAMOUNT",
              headerName: "Net Amount",
              align: "center",
              width: 120,
            },

            {
              field: "ENUSER",
              headerName: "Cashier Name",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Sales Transaction Report – Summary (133)",
        };
      case "134":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 },
            {
              field: "ORDERENDATE",
              headerName: " Date",
              align: "center",
              width: 150,
            },
            {
              field: "TIME",
              headerName: "Time",
              align: "center",
              width: 120,
            },
            {
              field: "ENUSER",
              headerName: "User Name",
              align: "left",
              width: 180,
            },
            {
              field: "INVNO",
              headerName: "Invoice Number",
              align: "center",
              width: 100,
            },
            {
              field: "CUSTOMER",
              headerName: "Customer Name",
              align: "left",
              width: 150,
            },
            {
              field: "GROSSAMOUNT",
              headerName: "Gross Amount",
              align: "right",
              width: 150,
            },
            {
              field: "DISCOUNT",
              headerName: "Discount",
              align: "right",
              width: 120,
            },
            // {
            //   field: "SHIPPINGAMOUNT",
            //   headerName: "Shipping Amount",
            //   align: "right",
            //   width: 150,
            // },
            {
              field: "ADJUSTAMOUNT",
              headerName: "Adjust Amount",
              align: "right",
              width: 150,
            },
            {
              field: "TAXAMOUNT",
              headerName: "Tax Amount",
              align: "right",
              width: 120,
            },
            {
              field: "CHARGEAMOUNT",
              headerName: "Charge Amount",
              align: "right",
              width: 150,
            },
            {
              field: "BILLAMOUNT",
              headerName: "Net Amount",
              align: "right",
              width: 150,
            },
            {
              field: "ADVANCEAMOUNT",
              headerName: "Advance Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CASHAMOUNT",
              headerName: "Cash Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CARDAMOUNT",
              headerName: "Card Amount",
              align: "right",
              width: 150,
            },
            {
              field: "SLIPAMOUNT",
              headerName: "Slip Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CHEQUEAMOUNT",
              headerName: "Cheque Amount",
              align: "right",
              width: 150,
            },
            {
              field: "GIFTAMOUNT",
              headerName: "Gift Amount",
              align: "right",
              width: 150,
            },
            {
              field: "LOYALTYAMOUNT",
              headerName: "Loyalty Amount",
              align: "right",
              width: 150,
            },
          ],
          reportName: "Sales Transaction Report – Detailed (134)",
        };

      case "135":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 },
            {
              field: "ORDERENDATE",
              headerName: " Date",
              align: "center",
              width: 150,
            },
            {
              field: "ENUSER",
              headerName: "User Name",
              align: "left",
              width: 180,
            },
            {
              field: "GROSSAMOUNT",
              headerName: "Gross Amount",
              align: "right",
              width: 150,
            },
            {
              field: "DISCOUNT",
              headerName: "Discount",
              align: "right",
              width: 120,
            },
            // {
            //   field: "SHIPPINGAMOUNT",
            //   headerName: "Shipping Amount",
            //   align: "right",
            //   width: 150,
            // },
            {
              field: "ADJUSTAMOUNT",
              headerName: "Adjust Amount",
              align: "right",
              width: 150,
            },
            {
              field: "TAXAMOUNT",
              headerName: "Tax Amount",
              align: "right",
              width: 120,
            },
            {
              field: "CHARGEAMOUNT",
              headerName: "other Charge Amount",
              align: "right",
              width: 150,
            },
            {
              field: "BILLAMOUNT",
              headerName: "Net Amount",
              align: "right",
              width: 150,
            },
            {
              field: "ADVANCEAMOUNT",
              headerName: "Advance Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CASHAMOUNT",
              headerName: "Cash Paid Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CARDAMOUNT",
              headerName: "Card paid Amount",
              align: "right",
              width: 150,
            },
            {
              field: "SLIPAMOUNT",
              headerName: "Bank Deposit",
              align: "right",
              width: 150,
            },
            {
              field: "CHEQUEAMOUNT",
              headerName: "Cheque Amount",
              align: "right",
              width: 150,
            },
            {
              field: "GIFTAMOUNT",
              headerName: "Redeem Gift Amount",
              align: "right",
              width: 150,
            },
            {
              field: "LOYALTYAMOUNT",
              headerName: "Redeem Loyalty Amount",
              align: "right",
              width: 150,
            },
          ],
          reportName: "Daily Sales Summary (135)",
        };

      case "136":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 },
            {
              field: "ITEMCODE",
              headerName: "Product Code",
              align: "left",
              width: 120,
            },
            {
              field: "ITEMNAME",
              headerName: "Product Name",
              align: "left",
              width: 180,
            },
            {
              field: "QTY",
              headerName: "Quantity Sold",
              align: "right",
              width: 120,
            },
            {
              field: "AMOUNT",
              headerName: "Gross Sales",
              align: "right",
              width: 150,
            },
          ],
          reportName: "Product Wise Sales – Summary (136)",
        };
      case "137":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 },
            {
              field: "ORDERENDATE",
              headerName: " Date",
              align: "center",
              width: 180,
            },
            {
              field: "ITEMCODE",
              headerName: "Product Code",
              align: "left",
              width: 120,
            },
            {
              field: "ITEMNAME",
              headerName: "Product Name",
              align: "left",
              width: 180,
            },
            {
              field: "QTY",
              headerName: "Quantity Sold",
              align: "right",
              width: 120,
            },
            {
              field: "AMOUNT",
              headerName: "Gross Sales",
              align: "right",
              width: 150,
            },
          ],
          reportName: "Product Wise Sales - Daily (137)",
        };

      case "138":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column

            {
              field: "CASHAMOUNT",
              headerName: "Total Cash Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CARDAMOUNT",
              headerName: "Total Card Amount",
              align: "right",
              width: 150,
            },
            {
              field: "SLIPAMOUNT",
              headerName: "Total Slip Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CHEQUEAMOUNT",
              headerName: "Total Cheque Amount",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Payment Wise Sales – Summary (138)",
        };

      case "139":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column
            {
              field: "ORDERENDATE",
              headerName: " Date",
              align: "center",
              width: 150,
            },
            {
              field: "CASHAMOUNT",
              headerName: "Total Cash Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CARDAMOUNT",
              headerName: "Total Card Amount",
              align: "right",
              width: 150,
            },
            {
              field: "SLIPAMOUNT",
              headerName: "Total Slip Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CHEQUEAMOUNT",
              headerName: "Total Cheque Amount",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Payment Wise Report – Daily (139)",
        };
      case "140":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column
            {
              field: "PAYDATE",
              headerName: " Date",
              align: "center",
              width: 150,
            },
            {
              field: "INVNO",
              headerName: "Invoice Number",
              align: "center",
              width: 120,
            },
            {
              field: "BILLAMOUNT",
              headerName: "Invoice Amount",
              align: "right",
              width: 150,
            },
            {
              field: "CUSTOMER",
              headerName: "Customer Name",
              align: "left",
              width: 150,
            },
            {
              field: "BANKNAME",
              headerName: "Bank Name",
              align: "left",
              width: 200,
            },
            {
              field: "TRANSACTIONID",
              headerName: "Transaction ID",
              align: "center",
              width: 150,
            },
            {
              field: "CARDAMOUNT",
              headerName: "Total Amount",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Card Payment Report (140)",
        };

      case "141":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column
            {
              field: "DEPOSITDATE",
              headerName: " Date",
              align: "left",
              width: 150,
            },
            {
              field: "ENUSER",
              headerName: "User",
              align: "left",
              width: 200,
            },
            {
              field: "OPENNINGBAL",
              headerName: "Opening Balance",
              align: "right",
              width: 150,
            },
            {
              field: "CASHDEPOSIT",
              headerName: "Cash Deposit",
              align: "right",
              width: 150,
            },

            {
              field: "CASHSALES",
              headerName: "Daily Cash Collections ",
              align: "right",
              width: 150,
            },
            {
              field: "FINALBAL",
              headerName: "Closing Balance ",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Cash Sales Reconciliation (141)",
        };

      case "142":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column
            {
              field: "PAYDATE",
              headerName: "Payment Date",
              align: "center",
              width: 150,
            },
            {
              field: "INVNO",
              headerName: "Invoice Number",
              align: "center",
              width: 120,
            },

            {
              field: "CUSTOMER",
              headerName: "Customer",
              align: "left",
              width: 150,
            },
            {
              field: "BANKNAME",
              headerName: "Bank Name",
              align: "left",
              width: 200,
            },
            // {
            //   field: "TRANSACTIONID",
            //   headerName: "Transaction ID",
            //   align: "center",
            //   width: 150,
            // },
            {
              field: "CHEQUEAMOUNT",
              headerName: "Cheque Amount",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Cheque Payment Report (142)",
        };

      case "143":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column
            {
              field: "GRNNO",
              headerName: "GRN Number",
              align: "left",
            },

            {
              field: "INVOICEDATE",
              headerName: "GRN Date",
              align: "center",
              width: 120,
            },
            {
              field: "SUPPLIER",
              headerName: "Supplier Name",
              align: "left",
              width: 150,
            },
            {
              field: "NETTAMT",
              headerName: " Total GRN Amount",
              align: "right",
              width: 150,
            },
            {
              field: "ENUSER",
              headerName: "User Name",
              align: "left",
              width: 150,
            },
          ],

          reportName: "Stock GRN Report – Simple (143)",
        };

      case "144":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column

            {
              field: "GRNNO",
              headerName: "GRN Number",
              align: "center",
              width: 120,
            },
            {
              field: "ENDATE",
              headerName: "GRN Date",
              align: "center",
              width: 120,
            },
            {
              field: "SUPPLIER",
              headerName: "Supplier",
              align: "left",
              width: 150,
            },
            {
              field: "SUPREFCODE",
              headerName: "Supplier Code",
              align: "center",
              width: 150,
            },
            {
              field: "INVOICENO",
              headerName: "Invoice Number",
              align: "center",
              width: 120,
            },
            {
              field: "INVOICEDATE",
              headerName: "Invoice Date",
              align: "center",
              width: 150,
            },

            {
              field: "MENUNAME",
              headerName: "Product Name ",
              align: "left",
              width: 150,
            },
            {
              field: "QTY",
              headerName: " GRN Quantity",
              align: "right",
              width: 120,
            },
            {
              field: "RATE",
              headerName: "Unit Price",
              align: "right",
              width: 120,
            },
            {
              field: "TOTAL",
              headerName: "Total Price",
              align: "right",
              width: 150,
            },
            {
              field: "SELLINGRATE",
              headerName: "Selling Rate",
              align: "right",
              width: 150,
            },
            {
              field: "EXPIRY",
              headerName: "Expiry Date",
              align: "center",
              width: 150,
            },
            {
              field: "DISCOUNT",
              headerName: "Discount",
              align: "right",
              width: 120,
            },
            {
              field: "TAXAMT",
              headerName: "Tax Amount",
              align: "right",
              width: 120,
            },
            {
              field: "GROSSAMT",
              headerName: "Gross Amount",
              align: "right",
              width: 150,
            },
            {
              field: "NETTAMT",
              headerName: "Net Amount",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Stock GRN Report – Detailed (144)",
        };

      case "145":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 },
            {
              field: "MENUCATCODE",
              headerName: "Product Category Code",
              align: "left",
              width: 150,
            },
            {
              field: "MENUCATNAME",
              headerName: "Product Category Name",
              align: "left",
              width: 200,
            },
            {
              field: "MENUCODE",
              headerName: "Product Code",
              align: "center",
              width: 150,
            },
            {
              field: "MENUNAME",
              headerName: "Product Name",
              align: "left",
              width: 200,
            },

            {
              field: "MEASURINGUNIT",
              headerName: "Unit of Measure ",
              align: "left",
              width: 150,
            },
            {
              field: "STOCKIN",
              headerName: "Stock In ",
              align: "right",
              width: 150,
              hide: true, // Hide this column
            },
            {
              field: "STOCKOUT",
              headerName: "Stock Out ",
              align: "right",
              width: 150,
              hideable: false, // Prevent users from showing the column manually
              hide: true, // Hide this column
            },
            {
              field: "STOCKINHAND",
              headerName: "Stock In Hand ",
              align: "right",
              width: 150,
            },
            {
              field: "MINORDERLEVEL",
              headerName: "Minimum Order Level",
              align: "right",
              width: 180,
            },
            {
              field: "AVGCOST",
              headerName: "Average Cost Price",
              align: "right",
              width: 150,
            },

            {
              field: "RATE",
              headerName: "Current Selling Price",
              align: "right",
              width: 150,
            },

            {
              field: "DIFFERENCE",
              headerName: "GP Margin (CSP-ACP)",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Product List Report (145)",
        };

      case "146":
        return {
          columns: [
            { field: "id", headerName: "Index", width: 90 }, // Auto-generated index column
            {
              field: "ITEMCODE",
              headerName: "Product Code",
              align: "left",
              width: 120,
            },
            {
              field: "ITEMNAME",
              headerName: "Product Name",
              align: "left",
              width: 180,
            },
            {
              field: "QTY",
              headerName: "Total Quantity Sold ",
              align: "right",
              width: 120,
            },
            {
              field: "AMOUNT",
              headerName: "Total Sales Amount ",
              align: "right",
              width: 150,
            },
          ],

          reportName: "Top Selling Report (146)",
        };

      default:
        return { columns: [], reportName: "Unknown Report" };
    }
  };

  const calculateRateMinusAvgCost = (row) => {
    if (!row) return 0; // Handle missing row gracefully
    const rate = row.RATE; // Default to 0 if RATE is undefined
    const avgCost = row.AVGCOST; // Default to 0 if AVGCOST is undefined
    return rate - avgCost; // Return the calculated difference
  };

  const rows = [
    ...reportDetails.map((row, index) => ({
      ...row,
      id: index + 1, // Auto-generate index starting from 1
    })),
    totalsRow, // Add the totalsRow at the end
  ];

  const groupByEnuser = (data) => {
    return data.reduce((acc, row) => {
      const enuser = row.ENUSER;
      if (!acc[enuser]) {
        acc[enuser] = [];
      }
      acc[enuser].push(row);
      return acc;
    }, {});
  };

  const groupByRestName = (details) => {
    return details.reduce((acc, curr) => {
      const restName = curr.RESTNAME;
      if (!acc[restName]) {
        acc[restName] = [];
      }
      acc[restName].push(curr);
      return acc;
    }, {});
  };

  const getRowId = (row, index) => {
    switch (reportId) {
      case "10":
        return row.ORDERCODE;
      case "11":
        return row.ORDERCODE;
      case "15":
        return row.ITEMCODE;
      case "133":
        return row.INVNO; // Ensure this field is unique
      case "136":
        return index; // Use the index as the unique ID
      // Add more cases as needed
      default:
        return row.INVNO || row.ORDERCODE || row.id || index; // Fallback to index for unique IDs
    }
  };

  const addRowIndex = (rows) => {
    return rows.map((row, index) => ({
      ...row,
      id: index + 1, // Auto-generate index starting from 1
    }));
  };

  return (
    <>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs2}
                position="fixed"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Reports</Link>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} md={6} p={2}>
              <Stack
                sx={styles.AI_fixedStack1}
                direction="row"
                justifyContent="flex-end"
                spacing={2}
                padding={1}
                ml={1}
              >
                <Button
                  onClick={gotToView}
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    background: theme.palette.primary.main,
                    color: theme.palette.text.buttontxt,
                    textTransform: "capitalize",
                    letterSpacing: "1px",
                    width: "80px",
                  }}
                >
                  Back
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </AppBar>
      <Box mt={15} ml={13} mr={4}>
     
        {/* <Grid container spacing={1}> */}
        {/* First Row: Back Button */}

        {/* Second Row: Select Location, From Date, To Date, Report Button */}
        <Grid container spacing={3} mr={15} mt={3}>
          {/* <Grid item xs={12} md={2}> */}
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={handleDateChange}
                inputFormat="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    sx: {
                      width: "100%",
                      "& .MuiInputBase-root": {
                        height: "40px",
                      },
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        color: theme.palette.primary.main, // Change icon color here
                      },
                    },
                  },
                  popper: {
                    sx: {
                      "& .MuiPaper-root": {
                        backgroundColor: theme.palette.background.paper, // Background color of the popper
                        boxShadow: "none", // Remove box shadow if needed
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={handleToDateChange}
                inputFormat="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    sx: {
                      width: "100%",
                      "& .MuiInputBase-root": {
                        height: "40px",
                      },
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        color: theme.palette.primary.main, // Change icon color here
                      },
                    },
                  },
                  popper: {
                    sx: {
                      "& .MuiPaper-root": {
                        backgroundColor: theme.palette.background.paper, // Background color of the popper
                        boxShadow: "none", // Remove box shadow if needed
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={8} sx={{ ...styles.right_align }}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchReportDetails}
              startIcon={<SettingsSuggestIcon />}
              disabled={isLoading}
              sx={{
                // ...styles.AI_fixedStack1,
                direction: "row",
                justifyContent: "flex-end",
                background: theme.palette.primary.main,
                color: theme.palette.text.buttontxt,
                textTransform: "capitalize",
                letterSpacing: "1px",
                width: "120px",
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Generate"}
            </Button>
          </Grid>
        </Grid>
        {/* </Grid> */}
     </Box>
      {/* Table Section */}
      <Box sx={styles.AddStyledBoxX} mt={2}>
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <Toolbar
              sx={{
                maxWidth: "100%",
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                background: theme.palette.background.paper,
              }}
            >
              <Typography
                variant="h6"
                id="tableTitle"
                component="div"
                sx={{
                  ...theme.typography.h2,
                  color: theme.palette.text.primary,
                  flex: "1 1 100%",
                  ml: 1,
                }}
              >
                <strong>{reports}</strong>
              </Typography>
              {/* Download Button */}
              {/* <Button
                id="MR_btn_srh"
                variant="contained"
                startIcon={<FileDownload />}
                sx={{
                  height: "40px", // Same height as others
                  minWidth: "100px",
                  textTransform: "capitalize",
                  letterSpacing: "1px",
                  background: theme.palette.primary.main,
                  color: theme.palette.text.buttontxt,
                }}
                onClick={downloadCSV}
              >
                Download
              </Button> */}
            </Toolbar>
            <div
              style={{
                height: "450px", // Set the height of the container
                overflow: "auto", // Enable scrolling for the container
                border: "1px solid #ccc", // Optional: Add a border for better visibility
                padding: "10px", // Optional: Add some padding
              }}
            >
              <DataGrid
                id="tablex"
               sx={{
                "@media print": {
          "MuiDataGrid-toolbarContainer": {
            
            display: "none !important", // Hide toolbar when printing
          },
        },
              }}
                rows={[
                  ...reportDetails.map((row, index) => ({
                    ...row,
                    id: index + 1, // Auto-generate index starting from 1
                    DIFFERENCE: calculateRowDifference(row, "RATE", "AVGCOST"),
                    STOCKINHAND: calculateRowDifference(
                      row,
                      "STOCKIN",
                      "STOCKOUT"
                    ),
                  })),
                  {
                    ...totalsRow,
                    id: "Total", // Ensure totalsRow has a unique id, like 'totals'
                    DIFFERENCE: calculateTotalDifference(
                      reportDetails,
                      "AVGCOST",
                      "RATE"
                    ),
                  },
                ]}
                columns={columns.map((col) => ({
                  ...col,
                  renderCell: (params) =>
                    renderCellValue(col.field, params.value),
                  renderHeader: () => <strong>{col.headerName}</strong>, // Bold the header name
                }))}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                slots={{
                  toolbar: () => (
                    <GridToolbar
                      slotProps={{
                        root: {
                          sx: {
                            backgroundColor: "#ffffff",
                            boxShadow: "none",
                            "@media print": {
                              backgroundColor: "rgba(185, 27, 27, 0.87)", // Change toolbar color when printing
                             
                            },
                          },
                        },
                      }}
                    />
                  ),
                }}
                
                loading={false} // Set this to true if data is being fetched asynchronously
                getRowId={(row, index) => getRowId(row, index)} // Use INVNO as the id
                disableRowSelectionOnClick
             
              />
            </div>
          </>
        )}
      </Box>
      {/* Snackbar for Error Messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ top: "10%", right: "10%" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ReportView;
